<template>
  <div class="w-full relative">
    <div v-show="showDropZone" :class="[
      'border rounded-md transition-colors duration-200 p-1',
      dropZoneActive ? 'border-sky-500 bg-sky-100 dark:bg-sky-900/40' : 'border-neutral-300 dark:border-neutral-600 bg-neutral-200/50 dark:bg-indigo-300/10',
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    ]"
    @drop.prevent="handleFileDrop"
    @dragover.prevent
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave">
      <div class="flex items-center flex-wrap">
        <div class="font-semibold text-sm text-indigo text-left p-1 pr-2">{{ title }}</div>
        <div v-if="multiple && documents.length > 0" 
             class="text-xs px-2 py-0.5 rounded-full border mr-2">
          {{ documents.length }}/{{ maxFiles }}
        </div>
        <div class="flex items-center p-1">
            <PlusIcon class="h-5 w-5 text-sky-500 text-lg mr-2" />
            <div class="pr-1">Drop a document or</div>
            <button class="bn-text" @click="triggerFileInput">
                <div>browse</div>
            </button>
        </div>
        <div v-if="description" class="w-full text-left whitespace-normal truncate px-1 opacity-70 py-0.5 font-light">
          {{ description }}
        </div>
      </div>
      <div class="text-left opacity-80 whitespace-normal truncate p-1">
        <span class="font-semibold">Allowed:</span> {{ allowedExtensions.join(', ') }} • <span class="font-semibold">Max:</span> {{ formatFileSize(maxFileSize) }}
      </div>
      <input 
        ref="fileInput"
        type="file" 
        :accept="acceptString"
        :multiple="multiple"
        @change="handleFileSelect"
        class="hidden"
        :disabled="disabled"
      />
      <div v-if="showOverlay" class="absolute inset-0 bg-white bg-opacity-60 dark:bg-black dark:bg-opacity-40 z-10 cursor-not-allowed flex items-center justify-center">
        <LoadingData v-if="isConvertingHeic" message="Converting HEIC..." />
        <span v-else class="text-sky font-semibold">Please wait...</span>
      </div>
    </div>
    <!-- Single File Display (existing behavior) -->
    <div v-if="!multiple && selectedFile" class="w-full p-1 border-b">
      <div class="w-full flex items-center rounded text-xs max-w-lg md:max-w-2xl">
        <div class="flex items-center space-x-2 min-w-0 flex-1 flex-grow flex-nowrap truncate">
          <DocumentIcon class="h-4 w-4 flex-shrink-0 text-sky" />
          <span class="truncate">{{ selectedFile.name }}</span>
          <span class="flex-shrink-0">({{ formatFileSize(selectedFile.size) }})</span>
        </div>
        <button 
          @click="removeFile"
          class="bn-icon-only ml-2 flex-shrink-0"
          title="Remove file"
          :disabled="disabled"
        >
          <TrashIcon />
        </button>
      </div>
    </div>

    <!-- Multiple Files Display -->
    <div v-if="multiple && !hideFileList && documents?.length > 0" class="w-full mt-1 space-y-0.5">
      <div v-for="(doc,index) in documents" :key="index" class="w-full flex items-center max-w-lg md:max-w-2xl truncate p-1 border-b">
        <DocumentIcon class="h-4 w-4 flex-shrink-0 text-sky mr-2" />
        <div class="flex-grow flex items-center flex-wrap truncate">
          <div class="flex-grow flex items-center flex-nowrap min-w-0 truncate">
            <div class="w-full whitespace-nowrap truncate p-1 text-left">
              {{doc.name}}
            </div>
            <div v-if="doc.size" class="flex-shrink-0">({{ formatFileSize(doc.size) }})</div>
          </div>
          <div v-if="showMetadata" class="flex-grow flex items-center flex-wrap p-1">
            <select 
              v-model="doc.docPurpose" 
              :class="{ 'border-red-300': !doc.docPurpose && documentTypes.length > 0 }"
              :disabled="disabled"
            >
              <option value="">Document Type</option>
              <option v-for="type in documentTypes" :key="type" :value="type">
                {{ type }}{{ requiredDocuments.includes(type) ? '*' : '' }}
              </option>
            </select>
          </div>
        </div>
        <div class="flex-shrink-0 ml-2">
          <button 
            @click="removeFile(index)"
            class="bn-icon-only"
            title="Remove file"
            :disabled="disabled"
          >
            <TrashIcon class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
    <div v-if="errorMessage" class="w-full text-left whitespace-normal truncatetext-xs text-red mt-1 hover:cursor-pointer hover:text-opacity-60" @click="errorMessage = ''">{{ errorMessage }}</div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { PlusIcon, TrashIcon } from '@heroicons/vue/outline';
import { DocumentIcon } from '@heroicons/vue/solid';
import heic2any from 'heic2any';
import LoadingData from '@/components/ui/LoadingData.vue';

export default {
  name: 'DocumentDropUpload',
  components: {
    PlusIcon,
    DocumentIcon,
    TrashIcon,
    LoadingData
  },
  props: {
    title: { type: String, default: 'Upload Document' },
    allowedTypes: { type: Array, default: () => ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'] },
    allowedExtensions: { type: Array, default: () => ['.pdf', '.doc', '.docx'] },
    maxFileSize: { type: Number, default: 10 * 1024 * 1024 }, // 10MB
    disabled: { type: Boolean, default: false },
    description: { type: String, default: '' },
    // NEW: Multiple file support
    multiple: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 10 },
    
    // NEW: Organization document features
    showMetadata: { type: Boolean, default: false },
    documentTypes: { type: Array, default: () => [] },
    requiredDocuments: { type: Array, default: () => [] },
    hideFileList: { type: Boolean, default: false },
    
    // NEW: v-model support
    modelValue: { type: [File, Array], default: null }
  },
  emits: [
    'file-selected', 
    'file-removed', 
    'update:modelValue', 
    'documents-changed', 
    'document-added', 
    'document-removed', 
    'validation-changed'
  ],
  setup(props, { emit, expose }) {
    const dropZoneActive = ref(false);
    const dragCounter = ref(0);
    const selectedFile = ref(null);
    const fileInput = ref(null);
    const errorMessage = ref('');
    const isConvertingHeic = ref(false);
    
    // NEW: Multiple file support
    const documents = ref([]);
    const documentValidation = ref({ isValid: true, errors: [], completeness: 100 });

    const acceptString = computed(() => {
      // Combine MIME types and extensions for better browser compatibility
      const mimeTypes = props.allowedTypes || [];
      const extensions = props.allowedExtensions || [];
      return [...mimeTypes, ...extensions].join(',');
    });
    
    // NEW: Computed properties for multiple file support
    const showDropZone = computed(() => 
      !props.multiple ? !selectedFile.value : documents.value.length < props.maxFiles
    );
    
    const allFiles = computed(() => 
      props.multiple ? documents.value : (selectedFile.value ? [selectedFile.value] : [])
    );

    const isHeic = (file) =>
      file?.type?.toLowerCase().includes('hei') || file?.name?.toLowerCase().endsWith('.heic');

    const convertHeicToJpeg = async (file) => {
      const baseName = file.name.replace(/\.[^.]+$/, '') || file.name;
      const result = await heic2any({ blob: file, toType: 'image/jpeg' });
      const blob = Array.isArray(result) ? result[0] : result;
      return new File([blob], `${baseName}.jpeg`, { type: 'image/jpeg' });
    };

    const validateFile = (file) => {
      if (typeof file.size === 'number' && file.size > props.maxFileSize) {
        return { valid: false, error: `File too large: ${formatFileSize(file.size)}` };
      }
      const isValidType = props.allowedTypes.includes(file.type) || 
        props.allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!isValidType) {
        return { valid: false, error: 'Unsupported file type' };
      }
      return { valid: true };
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
    };
    
    // NEW: Document validation for organization features
    const validateDocuments = () => {
      const validation = {
        isValid: true,
        errors: [],
        completeness: 0,
        requiredMissing: [],
        invalidDocuments: []
      };
      
      if (!props.multiple) {
        // Single file validation
        validation.isValid = !!selectedFile.value;
        validation.completeness = selectedFile.value ? 100 : 0;
        return validation;
      }
      
      // Multiple file validation
      if (props.requiredDocuments?.length > 0) {
        const submittedTypes = documents.value
          .filter(d => d.docPurpose && d.file)
          .map(d => d.docPurpose);
        const missing = props.requiredDocuments.filter(req => 
          !submittedTypes.includes(req)
        );
        
        if (missing.length > 0) {
          validation.isValid = false;
          validation.requiredMissing = missing;
          validation.errors.push(`Missing required documents: ${missing.join(', ')}`);
        }
      }
      
      // Check individual document validity
      documents.value.forEach((doc, index) => {
        if (props.showMetadata && !doc.docPurpose) {
          validation.isValid = false;
          validation.errors.push(`Document ${index + 1}: Type is required`);
          validation.invalidDocuments.push(index);
        }
      });
      
      // Calculate completeness
      const totalRequired = Math.max(props.requiredDocuments?.length || 1, 1);
      const completed = documents.value.filter(d => d.file && (!props.showMetadata || d.docPurpose)).length;
      validation.completeness = Math.min((completed / totalRequired) * 100, 100);
      
      return validation;
    };

    const triggerFileInput = () => {
      if (!props.disabled) fileInput.value?.click();
    };

    const processFileForAdd = async (file) => {
      if (isHeic(file)) {
        return convertHeicToJpeg(file);
      }
      return file;
    };

    const handleFileSelect = async (event) => {
      if (props.disabled) return;
      const files = Array.from(event.target.files || []);
      const hasHeic = files.some(isHeic);

      if (hasHeic) {
        isConvertingHeic.value = true;
      }

      try {
        if (props.multiple) {
          const documentsBefore = documents.value.length;
          const errors = [];

          for (const file of files) {
            const fileToAdd = await processFileForAdd(file);
            addFileWithoutEmit(fileToAdd, errors);
          }

          if (errors.length > 0) {
            const uniqueErrors = [...new Set(errors)];
            errorMessage.value = uniqueErrors.length === 1
              ? uniqueErrors[0]
              : `${errors.length} files had errors: ${uniqueErrors.join('; ')}`;
          }

          if (documents.value.length > documentsBefore) {
            emitDocumentsChanged();
          }
        } else if (files[0]) {
          const fileToAdd = await processFileForAdd(files[0]);
          addFile(fileToAdd);
        }
      } finally {
        isConvertingHeic.value = false;
      }

      event.target.value = '';
    };

    const handleFileDrop = async (event) => {
      if (props.disabled) return;
      dropZoneActive.value = false;
      const files = Array.from(event.dataTransfer.files || []);
      const hasHeic = files.some(isHeic);

      if (hasHeic) {
        isConvertingHeic.value = true;
      }

      try {
        if (props.multiple) {
          const documentsBefore = documents.value.length;
          const errors = [];

          for (const file of files) {
            const fileToAdd = await processFileForAdd(file);
            addFileWithoutEmit(fileToAdd, errors);
          }

          if (errors.length > 0) {
            const uniqueErrors = [...new Set(errors)];
            errorMessage.value = uniqueErrors.length === 1
              ? uniqueErrors[0]
              : `${errors.length} files had errors: ${uniqueErrors.join('; ')}`;
          }

          if (documents.value.length > documentsBefore) {
            emitDocumentsChanged();
          }
        } else if (files[0]) {
          const fileToAdd = await processFileForAdd(files[0]);
          addFile(fileToAdd);
        }
      } finally {
        isConvertingHeic.value = false;
      }
    };

    // Helper function to add file without emitting (for batch processing)
    const addFileWithoutEmit = (file, errors = null) => {
      if (props.disabled || !file) return null;
      
      const validation = validateFile(file);
      if (!validation.valid) {
        const error = `"${file.name}": ${validation.error}`;
        if (errors) {
          errors.push(error);
        } else {
          errorMessage.value = error;
        }
        return null;
      }

      if (props.multiple) {
        // Multiple file mode
        if (documents.value.length >= props.maxFiles) {
          const error = `Maximum ${props.maxFiles} files allowed`;
          if (errors) {
            errors.push(error);
          } else {
            errorMessage.value = error;
          }
          return null;
        }
        
        // Check for duplicate file (same name and size)
        const isDuplicate = documents.value.some(doc => 
          doc.name === file.name && doc.size === file.size
        );
        
        if (isDuplicate) {
          const error = `"${file.name}" has already been added`;
          if (errors) {
            errors.push(error);
          } else {
            errorMessage.value = error;
          }
          return null;
        }
        
        const documentObj = {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          docPurpose: '',
          expirationDate: null,
          description: '',
          required: props.requiredDocuments?.includes('') || false,
          category: 'other',
          isValid: !props.showMetadata, // Valid if no metadata required
          uploadStatus: 'pending'
        };
        
        documents.value.push(documentObj);
        // Only clear error if not in batch mode (errors array provided)
        if (!errors) {
          errorMessage.value = '';
        }
        return documentObj;
      }
      
      return null;
    };

    // Emit documents changed after batch processing
    const emitDocumentsChanged = () => {
      const docValidation = validateDocuments();
      documentValidation.value = docValidation;
      
      emit('update:modelValue', documents.value);
      emit('documents-changed', { documents: documents.value, validation: docValidation });
    };

    const addFile = (file) => {
      if (props.disabled) return;
      if (!file) return;
      
      const validation = validateFile(file);
      if (!validation.valid) {
        errorMessage.value = validation.error;
        return;
      }

      if (props.multiple) {
        // Multiple file mode - use batch processing
        const doc = addFileWithoutEmit(file);
        if (doc) {
          emitDocumentsChanged();
        }
      } else {
        // Single file mode (existing behavior)
        selectedFile.value = file;
        emit('update:modelValue', file);
        emit('file-selected', file);
        errorMessage.value = '';
      }
    };

    const removeFile = (index = null) => {
      if (props.disabled) return;
      
      if (props.multiple && index !== null) {
        // Remove specific document
        const removedDoc = documents.value[index];
        documents.value.splice(index, 1);
        
        const docValidation = validateDocuments();
        documentValidation.value = docValidation;
        
        emit('update:modelValue', documents.value);
        emit('documents-changed', { documents: documents.value, validation: docValidation });
        emit('document-removed', { index, document: removedDoc });
        
      } else {
        // Single file mode (existing behavior)
        selectedFile.value = null;
        emit('update:modelValue', null);
        emit('file-removed');
      }
      
      errorMessage.value = '';
    };

    const clearAll = () => {
      if (props.multiple) {
        documents.value = [];
        const docValidation = validateDocuments();
        documentValidation.value = docValidation;
        emit('update:modelValue', []);
        emit('documents-changed', { documents: [], validation: docValidation });
      } else {
        selectedFile.value = null;
        emit('update:modelValue', null);
        emit('file-removed');
      }
      errorMessage.value = '';
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const handleDragEnter = () => {
      if (props.disabled) return;
      dragCounter.value++;
      dropZoneActive.value = true;
    };
    const handleDragLeave = () => {
      if (props.disabled) return;
      dragCounter.value--;
      if (dragCounter.value <= 0) {
        dropZoneActive.value = false;
        dragCounter.value = 0;
      }
    };

    const showOverlay = computed(() => props.disabled || isConvertingHeic.value);

    const exposedMethods = {
      removeFile,
      clearAll
    };
    
    // Explicitly expose methods to parent components
    expose(exposedMethods);
    
    return {
      dropZoneActive,
      dragCounter,
      selectedFile,
      fileInput,
      triggerFileInput,
      handleFileSelect,
      handleFileDrop,
      handleDragEnter,
      handleDragLeave,
      removeFile,
      formatFileSize,
      showOverlay,
      isConvertingHeic,
      errorMessage,
      acceptString,
      // NEW: Multiple file support
      documents,
      documentValidation,
      showDropZone,
      allFiles,
      validateDocuments
    };
  }
};
</script>
